import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedHistoryEntry, DirectedStyle } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { howMadeMessages as messages } from '../messages/howMadeMessages';
import { storyMessages } from '../messages/storyMessages';
import { DIRECTED_STEP_NAMES } from '../shell/DirectedStepper';
import { StylePreviewPicture } from '../steps/StylePreviewPicture';
import { useDirectedCastNames } from '../useDirectedCastNames';
import { howMadeStylePreviewUrl, isEditedPick, isPickedOption, pickedRoundIndex, styleRounds } from './howMadeRules';
import { HowMadeRounds } from './HowMadeRounds';
import { HowMadeSection } from './HowMadeSection';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for "How it was made"; each part uses its own slice of it */
import styles from './HowMade.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ROWS = [
  { key: 'camera', name: storyMessages.camera },
  { key: 'colourAndLight', name: storyMessages.colourAndLight },
  { key: 'pacing', name: storyMessages.pacing },
] as const;

/**
 * Step 3 of "How it was made" (design l1): the styles of the director, round by round, with the
 * picked one marked. A style that the artist wrote shows as its own card. Each card shows the
 * preview still of its look when one is ready (`howMadeStylePreviewUrl`); else no picture box.
 */
const HowMadeStylesComponent: React.FC<{ entries: readonly DirectedHistoryEntry[] }> = ({ entries }) => {
  const intl = useIntl();
  const { video } = useDirectedVideoContext();
  const { named } = useDirectedCastNames();
  const rounds = useMemo(() => styleRounds(entries, video), [entries, video]);
  const picked = pickedRoundIndex(rounds, video.style);
  const [shown, setShown] = useState<number | null>(null);
  const index = shown ?? picked;
  const options: DirectedStyle[] = useMemo(() => {
    const list = rounds[index]?.options ?? [];
    // A style that the artist wrote is in no round: it shows after the round that was picked.
    const own = video.style !== null && !rounds.some((round) => round.options.some((s) => s.id === video.style?.id));
    return own && video.style !== null && index === picked ? [...list, video.style] : list;
  }, [index, picked, rounds, video.style]);
  const total = rounds.reduce((sum, item) => sum + item.options.length, 0);

  return (
    <HowMadeSection
      step="style"
      eyebrow={intl.formatMessage(messages.stepEyebrow, {
        number: 3,
        name: intl.formatMessage(DIRECTED_STEP_NAMES.style),
      })}
      title={
        video.style === null
          ? intl.formatMessage(messages.noPick)
          : intl.formatMessage(messages.styleTitle, { title: named(video.style.title) })
      }
      text={intl.formatMessage(messages.styleText, { styles: total, rounds: Math.max(1, rounds.length) })}
      aside={<HowMadeRounds count={rounds.length} shown={index} picked={picked} onShow={setShown} />}
    >
      <div className={styles.options}>
        {options.map((style, at) => {
          const isOn = isPickedOption(style, video.style);
          const shownStyle = isOn && video.style !== null ? video.style : style;
          const isOwn = at >= (rounds[index]?.options.length ?? 0);
          const previewUrl = howMadeStylePreviewUrl(style, rounds[index], video, entries);
          return (
            <div
              key={style.id}
              className={clsx(styles.option, isOn ? styles.optionOn : styles.optionDim)}
              data-picked={isOn}
            >
              {previewUrl !== null && (
                <StylePreviewPicture
                  preview={{ styleId: style.id, status: 'ready', imageUrl: previewUrl }}
                  title={named(shownStyle.title)}
                />
              )}
              <div className={styles.optionHead}>
                <span className={styles.optionNumber}>
                  {isOwn
                    ? intl.formatMessage(messages.ownStyle)
                    : intl.formatMessage(messages.style, { number: at + 1 })}
                </span>
                {isOn && (
                  <span className={styles.pick}>
                    {intl.formatMessage(isEditedPick(style, video.style) ? messages.pickedEdited : messages.picked)}
                  </span>
                )}
              </div>
              <div className={styles.optionTitle}>{named(shownStyle.title)}</div>
              <dl className={styles.styleRows}>
                {ROWS.map((row) => (
                  <div key={row.key} className={styles.styleRow}>
                    <dt>{intl.formatMessage(row.name)}</dt>
                    <dd>{named(shownStyle[row.key])}</dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}
      </div>
    </HowMadeSection>
  );
};

export const HowMadeStyles = TypedMemo(HowMadeStylesComponent);
